import { AnnotationEditorType, PDFEditor, PDFEditorConnector, usePDFEditorConnector } from '@ethsign/pdf-editor';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { dragImage } from '@/constants/editor';
import { IContact, ICurrentUser, IField, SendType } from '@/types';
import { BufferToFile, convertXmlToJSON, eventBus, fileToBuffer, generateSignPic, isSameUser, setStyle } from '@/utils';

import { FieldSettingsDrawer } from '../FieldSettingsDrawer';

import { SignatureStyleModal } from './SignatureStyleModal';
import '@ethsign/pdf-editor/style.css';

interface Props {
  accounts?: IContact[];
  file?: File;
  //签署框信息
  xfdfString?: string;
  //签署信息
  xfdfStrings?: string[];
  onChange?: (xfdfString: string, annotations: any) => void;
  isShow: boolean;
  onLoaded?: (instance: PDFEditorConnector) => void;
  onSelectedFieldChange?: (id?: string) => void;
  contractName?: string;
  sendType?: SendType;
  type: 'prepare' | 'sign' | 'preview' | 'prepareAndSign';
  currentUser?: any;
  onFieldsChange?: (type: 'delete' | 'add' | 'modify', annot?: any) => void;
  user?: ICurrentUser;
  isMatchUser?: boolean;
  isVoid?: boolean;
}

export type ContractEditorRef = {
  instance: any;
  dragEnd: (e: any, instance: any) => void;
  loadDocument: (addBlankPage: boolean) => void;
  getFile: () => File | undefined;
  attachAnnots: (fields: IField[], file: File) => void;
  addAnnotation: (info: { type: keyof typeof dragImage; name: string; address: string; email: string }) => void;
};
export const ContractEditor = forwardRef(function ContractEditor(props: Props, ref: any) {
  const {
    accounts,
    file,
    onChange,
    xfdfString,
    onLoaded,
    onSelectedFieldChange,
    contractName,
    sendType,
    type,
    xfdfStrings,
    isMatchUser,
    currentUser,
    onFieldsChange,
    user,
    isVoid
  } = props;

  const [instance, setInstance] = useState<PDFEditorConnector>();
  const [pdfFile, setPdfFile] = useState<any>();
  const [tempPdfFile, setTempPdfFile] = useState<any>();
  const tempAnnots = useRef<any>();
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<any>('');
  const [openFieldSettings, setOpenFieldSettings] = useState(false);

  const selectAnnotation: any = useMemo(() => instance?.getAnnotationById(selectedField), [selectedField, instance]);

  const isShowAnnots = useCallback(
    (custom: any) => {
      return custom && (isMatchUser ? isSameUser(currentUser, custom) : true);
    },
    [isMatchUser, currentUser]
  );
  const dragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    return false;
  };

  const addField = async (
    info: {
      type: keyof typeof dragImage;
      name: string;
      address: string;
      email: string;
    },
    image: string,
    instance: any
  ) => {
    const { type } = info;

    if (type === 'readonlyText') {
      instance.setAnnotationEditorType(AnnotationEditorType.FREETEXT, { data: info });
    } else {
      instance.setAnnotationEditorType(AnnotationEditorType.STAMP, {
        bitmapFile: image,
        width: 94 * 0.7,
        height: 26 * 0.7,
        data: info
      });
    }
  };
  const applyFields = async (
    annot: any,
    instance: PDFEditorConnector,
    img?: { data: string; width: number; height: number }
  ): Promise<void> => {
    if (typeof annot?.data === 'object') {
      // create a form field based on the type of annotation
      if (annot.data?.type === 'text') {
        annot.show(false);
        const data = annot.serialize(false, true);

        data.annotationType = AnnotationEditorType.FREETEXT;
        data.data.linkId = annot.id;
        data.editorId = annot.id + '_linked';
        data.fontSize = 12;
        data.color = [0, 0, 0];
        data.editable = true;
        data.isDraggable = false;
        data.data.completed = true;
        data.noAutoFocus = false;
        instance.setAnnotations([data]);
      } else if (annot.data.type === 'signature') {
        annot.show(false);
        const data = annot.serialize(false, true);
        data.bitmapFile = img?.data;
        data.bitmapId = null;
        data.width = img?.width;
        data.height = img?.height;
        data.editorId = null;
        data.data.completed = true;
        data.data.linkId = annot.id;
        data.editorId = annot.id + '_linked';
        data.editable = true;
        data.isDraggable = false;
        data.resizers = ['bottomRight'];
        data.noAutoFocus = false;
        instance.setAnnotations([data]);
      } else if (annot.data.type === 'date') {
        annot.show(false);
        const data = annot.serialize(false, true);
        data.annotationType = AnnotationEditorType.DATE;
        data.data.linkId = annot.id;
        data.editorId = annot.id + '_linked';
        data.fontSize = 12;
        data.color = [0, 0, 0];
        data.editable = true;
        data.isDraggable = false;
        data.data.completed = true;
        data.noAutoFocus = false;
        data.value = new Date().toLocaleDateString();
        instance.setAnnotations([data]);
      } else {
        return;
      }

      //annot.Hidden = true;
    }
  };
  const dragEnd = (_: MouseEvent, instance: any) => {
    if (!instance) return;
    const dragimage = document.getElementById('dragimage-container')!;
    document.body.removeChild(dragimage as Node);
  };
  useImperativeHandle(
    ref,
    () => {
      return {
        instance,
        dragEnd,
        loadDocument: async (addBlankPage: boolean) => {
          if (instance && pdfFile) {
            instance.removeAllAnnotations();
            if (addBlankPage) {
              const newPdfBytes = await instance.addEmptyPage();
              if (newPdfBytes) {
                const newPdfFile = BufferToFile(newPdfBytes, contractName);
                setTempPdfFile(newPdfFile);
              }
            } else {
              instance.open(URL.createObjectURL(pdfFile));
              setTempPdfFile(pdfFile);
            }

            setDocumentLoaded(false);
            setOpenFieldSettings(false);
          }
        },
        getFile: () => {
          return tempPdfFile;
        },
        attachAnnots: async (fields: IField[], blankFile: File) => {
          const handle = async (isAsync?: boolean) => {
            if (instance && fields.length) {
              const annots: any = [];
              await Promise.all(
                fields.map(async (field, i) => {
                  const searchTerm = field.value;
                  const res = await instance.locate(searchTerm);
                  if (!(res && res[0])) return;
                  const { rect, pageIndex } = res[0];
                  const width = rect[2] - rect[0];
                  const height = (26 / 94) * width;
                  const newRect = [rect[0], rect[1] - height * 0.5, rect[0] + width, rect[1] + height * 0.5];
                  const data = {
                    rect: newRect,
                    pageIndex,
                    data: field.custom,
                    editorId: `pdfjs_${field.custom.type}_${i}`,
                    rotation: 0,
                    annotationType: AnnotationEditorType.STAMP,
                    isSvg: true,
                    editable: true,
                    noAutoFocus: true,
                    bitmapFile: dragImage[field.custom.type as keyof typeof dragImage].replaceAll('color', field.color)
                  };
                  annots.push(data);
                })
              );

              if (annots?.length > 0) {
                console.log(annots, 'attachAnnots');
                tempAnnots.current = { annots, file: blankFile };
                if (isAsync) {
                  instance.removeAllAnnotations();
                  instance.setAnnotations(annots);
                }
              }
            }
          };
          return new Promise((resolve) => {
            if (documentLoaded) {
              handle().then(() => {
                resolve(true);
              });
            } else {
              eventBus.once('documentLoaded', async () => {
                await handle(true);
                resolve(true);
              });
            }
          });
        },
        addAnnotation: async ({ type, name, address, email, color }: any) => {
          addField(
            { type, name, address, email },
            dragImage[type as keyof typeof dragImage].replaceAll('color', color as string),
            instance
          );
        }
      };
    },
    [instance, dragEnd, pdfFile, tempPdfFile, documentLoaded]
  );

  useEffect(() => {
    onSelectedFieldChange && onSelectedFieldChange(selectedField);
  }, [selectedField]);

  useEffect(() => {
    // 加载文档
    if (instance) {
      const onDocumentLoaded = async () => {
        let annots = (xfdfString && JSON.parse(await convertXmlToJSON(xfdfString))) || [];
        if (type === 'sign' || type === 'preview' || type === 'prepareAndSign') {
          // 解析需要签署的字段
          if (type === 'sign') {
            instance?.eventBus.on('scalechanging', () => {
              instance.getAllAnnotations().forEach((annot: any) => {
                if (isShowAnnots(annot.data) && !annot.data?.completed) {
                  const item = instance.getAnnotationById(annot.editorId);
                  if (!item) return;
                  const element = instance.iframeWindow.document.getElementById(item.id);
                  if (element) {
                    const customElement = instance.iframeWindow.document.getElementById('customElement' + item.id);
                    if (customElement) {
                      setStyle(customElement, {
                        top: element.style.top,
                        marginTop: -(38 - element.getBoundingClientRect().height) / 2 + 'px',
                        left: -90 + 'px'
                      });
                    }
                  }
                }
              });
            });
            annots = annots
              .filter((annot: any) => isShowAnnots(annot.data) || annot.data?.type === 'readonlyText')
              .map((item: any) => {
                item.isResizable = false;
                item.editable = false;
                item.bitmapFile = dragImage[item.data?.type as keyof typeof dragImage].replaceAll('color', 'FDB022');
                return item;
              });
          }
          if (type === 'preview') {
            annots = annots
              .filter((annot: any) => annot.data?.type === 'readonlyText')
              .map((item: any) => {
                item.editable = false;
                return item;
              });
          }
        }
        if (tempAnnots.current && tempAnnots.current.file === file) {
          annots = tempAnnots.current.annots;
          tempAnnots.current = null;
          instance.removeAllAnnotations();
        }

        instance.setAnnotations(annots);
        if (type === 'preview') {
          // 只读模式
          instance.setAnnotationEditorType(AnnotationEditorType.NONE);
        }
        onLoaded && onLoaded(instance);
        setDocumentLoaded(true);
        eventBus.emit('documentLoaded');
      };
      instance?.eventBus.on('pagesloaded', onDocumentLoaded);
      return () => {
        instance?.eventBus.off('pagesloaded', onDocumentLoaded);
      };
    }
  }, [instance, type, xfdfString, isShowAnnots, file]);

  useEffect(() => {
    if (instance && type !== 'prepare') {
      const annotationChanged = ({ action, data }: any) => {
        //设置签署框背景图片和位置指示
        if (action === 'add') {
          if (data?.length === 1 && data[0].data?.linkId) {
            onFieldsChange && onFieldsChange('add', data[0]);
          } else {
            const iframeDoc = instance.iframeWindow.document;
            data.forEach((annot: any) => {
              const custom = annot.data || {};
              //本人签署款除了readonlyText加上markder
              if (isShowAnnots(custom) && !custom.completed && custom.type !== 'readonlyText' && type === 'sign') {
                const pageContainer = iframeDoc.getElementsByClassName('annotationEditorLayer')?.[annot.pageIndex];
                const customElement = iframeDoc.createElement('div');
                customElement.id = 'customElement' + annot.id;
                const element = iframeDoc.getElementById(annot.id);
                setStyle(customElement, {
                  id: 'customElement' + annot.Id,
                  background: `url("/img/sign-here.svg")`,
                  backgroundSize: 'cover',
                  width: '90px',
                  height: '38px',
                  position: 'absolute',
                  top: element?.style.top,
                  marginTop: -10 + 'px',
                  left: -90 + 'px',
                  zIndex: '99'
                });
                pageContainer?.appendChild(customElement);
              }
            });
          }
        }
        if (action === 'delete') {
          const annot = instance.getAnnotationById(data.data.linkId);
          //prepareAndSign类型删除签署框那么重置 fields
          if (type === 'prepareAndSign' && data.data.linkId) {
            onFieldsChange && onFieldsChange('delete');
            return;
          }
          if (annot) {
            annot.show(true);
            annot.data.completed = false;
            onFieldsChange && onFieldsChange('delete', annot);
          }
        }
      };

      const annotationSelected = ({ action, data }: any) => {
        const { completed, type } = data.data || {};
        if (action === 'selected') {
          if (!completed) {
            switch (type) {
              case 'signature':
                setSignatureModalOpen(true);
                break;
              case 'date':
              case 'text':
                setTimeout(() => {
                  applyFields(data, instance);
                }, 50);
            }
          }
          setSelectedField(data.id || data.editorId);
        } else if (action === 'deselected') {
          setSelectedField('');
        }
      };
      instance.eventBus.on('annotationEditorSelected', annotationSelected);
      instance.eventBus.on('annotationEditorChange', annotationChanged);

      return () => {
        instance.eventBus.off('annotationEditorSelected', annotationSelected);
        instance.eventBus.off('annotationEditorChange', annotationChanged);
      };
    }
  }, [instance, type, isShowAnnots]);

  useEffect(() => {
    if (instance && type === 'prepare' && documentLoaded) {
      const addressArray = accounts?.map((account) => account.address);
      const emailsArray = accounts?.map((account) => account.email);
      const handdleAnnotations = async () => {
        const toDelete: any = [];
        const signAnotations: any = [];
        instance.getAllAnnotations().forEach((annot: any) => {
          if (sendType === SendType.invite) {
            if (
              addressArray?.includes(annot.data?.address) ||
              (annot.data?.email && emailsArray?.includes(annot.data?.email))
            ) {
              signAnotations.push(annot);
            } else {
              if (annot.data) {
                toDelete.push(annot);
              }
            }
          } else {
            // 如果不是 invite模式不需要地址匹配
            if (annot.data) {
              signAnotations.push(annot);
            }
          }
        });
        toDelete.forEach((element: any) => {
          instance.removeAnnotationById(element.editorId);
        });
        onChange && onChange(JSON.stringify(signAnotations), signAnotations);
      };
      const annotationSelected = ({ action, data }: any) => {
        if (action === 'selected') {
          const annot = data;
          if (annot.data?.type === 'text' || annot.data?.type === 'date') {
            setSelectedField(annot.id);
            setOpenFieldSettings(true);
          }
        } else {
          setOpenFieldSettings(false);
          setSelectedField('');
        }
      };
      const annotationChanged = async () => {
        await handdleAnnotations();
      };
      instance.eventBus.on('annotationEditorSelected', annotationSelected);
      instance.eventBus.on('annotationEditorChange', annotationChanged);

      documentLoaded && handdleAnnotations();
      return () => {
        instance.eventBus.off('annotationEditorSelected', annotationSelected);
        instance.eventBus.off('annotationEditorChange', annotationChanged);
      };
    }
  }, [instance, JSON.stringify(accounts), documentLoaded, sendType, type]);

  useEffect(() => {
    if (instance && (type === 'prepare' || type === 'sign' || type === 'prepareAndSign')) {
      const { iframeWindow } = instance;
      const iframeDoc = iframeWindow.document.body;
      iframeDoc.addEventListener('dragover', dragOver);
      const moveFuncInFrame = (e: MouseEvent) => {
        const viewer: any = document.getElementsByClassName('webviewer')[0];
        const dragimage = document.getElementById('dragimage-container');
        if (!dragimage) return;
        const offset = viewer.getBoundingClientRect();
        dragimage.style.transform = `translate3D(${offset.left + e.x + 2}px,${offset.top + e.y + 2}px,0)`;
      };
      iframeDoc.addEventListener('mousemove', moveFuncInFrame);
      const clickFuncInframe = (e: MouseEvent) => {
        const dragimage = document.getElementById('dragimage-container');
        if (dragimage) {
          dragEnd(e, instance);
        }
      };
      iframeDoc.addEventListener('click', clickFuncInframe, true);
      const moveFunc = (e: { x: number; y: number }) => {
        const dragimage = document.getElementById('dragimage-container');
        if (!dragimage) return;

        dragimage.style.transform = `translate3D(${e.x + 2}px,${e.y + 2}px,0)`;
      };
      document.addEventListener('mousemove', moveFunc, false);
      const clickFunc = (e: MouseEvent) => {
        const dragimage = document.getElementById('dragimage-container');
        if (dragimage) {
          const viewer: any = document.getElementsByClassName('webviewer')[0];
          const offset = viewer.getBoundingClientRect();
          dragEnd({ ...e, pageX: e.pageX - offset.left, pageY: e.pageY - offset.top }, instance);
        }
      };
      document.addEventListener('click', clickFunc, true);
      return () => {
        document.removeEventListener('click', clickFunc, true);
        document.removeEventListener('mousemove', moveFunc);
        iframeDoc.removeEventListener('mousemove', moveFuncInFrame);
        iframeDoc.removeEventListener('click', clickFuncInframe, true);
        iframeDoc.removeEventListener('dragOver', dragOver);
      };
    }
  }, [instance, type]);

  useEffect(() => {
    if (instance && file) {
      const loadDocument = async () => {
        const fileArrayBuffer = await fileToBuffer(file);
        const pdfFile = BufferToFile(fileArrayBuffer, contractName);
        instance.removeAllAnnotations();
        instance.open(URL.createObjectURL(pdfFile));
        setPdfFile(pdfFile);
        setTempPdfFile(pdfFile);
        setDocumentLoaded(false);
      };
      loadDocument();
    }
  }, [instance, file]);

  useEffect(() => {
    if (instance && documentLoaded && type !== 'prepare') {
      type === 'preview' && instance.removeAllAnnotations();
      xfdfStrings?.forEach((xfdfString) => {
        if (xfdfString) {
          let annots = JSON.parse(xfdfString);
          annots = annots.map((item: any) => {
            item.editable = false;

            return item;
          });

          instance.setAnnotations(annots);
        }
      });
    }
  }, [xfdfStrings, instance, documentLoaded, type, isVoid]);

  useEffect(() => {
    const setVoid = () => {
      if (isVoid && instance && documentLoaded) {
        const voidDiv = instance.iframeWindow.document.getElementById('void');
        if (voidDiv) return;
        const containter = instance.iframeWindow.document.getElementById('viewerContainer');
        const innerHtml = `<div>VOID</div>`;
        const div = document.createElement('div');
        div.id = 'void';
        div.innerHTML = innerHtml;
        setStyle(div, {
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#8e8e8e',
          fontSize: '100px',
          transform: 'rotate(-45deg)',
          fonWetight: 'bold'
        });
        containter?.appendChild(div);
      }
    };
    setVoid();
  }, [instance, documentLoaded, isVoid]);

  const connector = usePDFEditorConnector({
    viewerUrl: `/pdf-viewer/web/viewer.html?file=''#locale=en-US`
  });
  return (
    <>
      <div className="h-full webviewer">
        {file && (
          <PDFEditor
            connector={connector}
            title={contractName}
            onReady={(instance: any) => {
              props.onLoaded?.(instance);
              setInstance(instance);
            }}
          />
        )}
      </div>
      {(type === 'sign' || type === 'prepareAndSign') && (
        <SignatureStyleModal
          open={signatureModalOpen}
          onClose={() => {
            // 如果是prepareAndSign类型需要把原来的签署框删除
            if (type === 'prepareAndSign') {
              instance?.removeAnnotationById(selectedField);
            }
          }}
          onOpenChange={(open) => {
            setSignatureModalOpen(open);
          }}
          onConfirm={async (img) => {
            const annot = instance?.getAnnotationById(selectedField);
            applyFields(annot, instance!, await generateSignPic(img, user!));
          }}
        />
      )}
      {type === 'prepare' && (
        <FieldSettingsDrawer
          key={selectedField}
          value={selectAnnotation?.data}
          onValueChange={(value) => {
            if (selectAnnotation) {
              const annot = selectAnnotation;

              annot.data = {
                ...(annot.data || {}),
                label: value.label,
                required: value.required
              };
            }
          }}
          open={openFieldSettings}
        ></FieldSettingsDrawer>
      )}
    </>
  );
});
